import { isRestrictedSessionToken } from '@packages/auth';
import type { AnonymousUiUsageEvent } from '@packages/types';

export interface AnonymousUiUsageSenderDeps {
  /** Sends one event with no token and no cookies. */
  send: (event: AnonymousUiUsageEvent) => Promise<unknown>;
  /** The session token a request would carry. Read only to decide whether to send at all. */
  readToken: () => string | null;
}

/**
 * The delivery path of the anonymous channel: no queue and no timer. An event leaves as soon as
 * it is accepted, as its own request, so nothing can hold it until after a login and no shared
 * request links one visitor's steps. The token is read once, to drop the event for a restricted
 * (impersonation) session; it is never sent and never stamped on the event, so a token stored
 * after the event left changes nothing about it.
 *
 * Delivery is best effort: a failure is dropped, never retried and never reported.
 */
export function createAnonymousUiUsageSender(deps: AnonymousUiUsageSenderDeps): (event: AnonymousUiUsageEvent) => void {
  return (event) => {
    const token = deps.readToken();
    if (token !== null && isRestrictedSessionToken(token)) return;
    // Dropped on failure by design; see the function doc.
    deps.send(event).catch(() => undefined);
  };
}
